export const getVideoResolution = async (file: File): Promise<{ height: number; duration: number }> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.onloadedmetadata = async () => {
      let h = video.videoHeight;
      if (video.videoHeight === 0) {
        const { height } = await getMp4DimensionsFast(file);
        h = height;
      }
      resolve({ height: h, duration: video.duration });
    };
    video.onerror = reject;
    video.src = URL.createObjectURL(file);
  });
};

type QuickTimeBox = {
  start: number;
  size: number;
  headerSize: number;
  type: string;
  contentStart: number;
  end: number;
};

export type UploadVideoTrackInspection = {
  sampleDescriptionIndexes: number[];
  codecTypes: string[];
  codecs: string[];
  resolutions: string[];
  fpsValues: number[];
  averageFps: number | null;
};

export type UploadAudioTrackInspection = {
  sampleDescriptionIndexes: number[];
  sampleCount: number;
  hasSampleBytes: boolean | null;
};

export type UploadVideoInspection = {
  tracks: UploadVideoTrackInspection[];
  audioTracks: UploadAudioTrackInspection[];
  codecTypes: string[];
  codecs: string[];
  resolutions: string[];
  fpsValues: number[];
};

const readChunk = (file: File, start: number, len: number) =>
  new Promise<ArrayBuffer>((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result as ArrayBuffer);
    fr.onerror = rej;
    fr.readAsArrayBuffer(file.slice(start, start + len));
  });

const readU16 = (dv: DataView, o: number) => dv.getUint16(o);
const readU32 = (dv: DataView, o: number) => dv.getUint32(o);
const readU64 = (dv: DataView, o: number) => Number(dv.getBigUint64(o));
const toStr = (dv: DataView, o: number) =>
  String.fromCharCode(dv.getUint8(o), dv.getUint8(o + 1), dv.getUint8(o + 2), dv.getUint8(o + 3));

const normalizeCodecType = (codecType: string) => String(codecType || '').trim().toLowerCase();

const normalizeCodecLabel = (codecType: string) => {
  const normalized = normalizeCodecType(codecType);
  if (['avc1', 'avc3'].includes(normalized)) {
    return 'H.264';
  }
  if (['hvc1', 'hev1'].includes(normalized)) {
    return 'HEVC';
  }
  return normalized ? normalized.toUpperCase() : 'UNKNOWN';
};

const normalizeFpsValue = (fps: number): number | null => {
  if (!Number.isFinite(fps) || fps <= 0) {
    return null;
  }

  const commonFpsValues = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60];
  for (const value of commonFpsValues) {
    if (Math.abs(fps - value) < 0.05) {
      return value;
    }
  }

  return Number(fps.toFixed(3));
};

const uniqueStrings = (values: Array<string | null | undefined>) =>
  Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)));

const uniqueNumbers = (values: Array<number | null | undefined>) =>
  Array.from(
    new Set(
      values
        .map((value) => (Number.isFinite(Number(value)) ? Number(value) : null))
        .filter((value): value is number => value !== null),
    ),
  );

const readBox = (dv: DataView, start: number, end: number): QuickTimeBox | null => {
  if (start + 8 > end) {
    return null;
  }

  let size = readU32(dv, start);
  const type = toStr(dv, start + 4);
  let headerSize = 8;

  if (size === 1) {
    if (start + 16 > end) {
      return null;
    }
    size = readU64(dv, start + 8);
    headerSize = 16;
  } else if (size === 0) {
    size = end - start;
  }

  if (!size || size < headerSize || start + size > end) {
    return null;
  }

  return {
    start,
    size,
    headerSize,
    type,
    contentStart: start + headerSize,
    end: start + size,
  };
};

const listChildBoxes = (dv: DataView, start: number, end: number, wantedType?: string) => {
  const boxes: QuickTimeBox[] = [];
  let offset = start;

  while (offset + 8 <= end) {
    const box = readBox(dv, offset, end);
    if (!box) {
      break;
    }
    if (!wantedType || box.type === wantedType) {
      boxes.push(box);
    }
    offset = box.end;
  }

  return boxes;
};

const getFirstChildBox = (dv: DataView, parent: QuickTimeBox, type: string) =>
  listChildBoxes(dv, parent.contentStart, parent.end, type)[0] || null;

const readQuickTimeMoovBuffer = async (file: File) => {
  let offset = 0;
  const fileSize = file.size;

  while (offset + 8 <= fileSize) {
    const headerBuffer = await readChunk(file, offset, 16);
    const headerView = new DataView(headerBuffer);
    let size = readU32(headerView, 0);
    const type = toStr(headerView, 4);
    let headerSize = 8;

    if (size === 1) {
      size = readU64(headerView, 8);
      headerSize = 16;
    } else if (size === 0) {
      size = fileSize - offset;
    }

    if (!size || size < headerSize) {
      throw new Error('Corrupt MP4 box');
    }

    if (type === 'moov') {
      const moovBuffer = await readChunk(file, offset, size);
      const moovView = new DataView(moovBuffer);
      const moovBox = readBox(moovView, 0, moovBuffer.byteLength);

      if (!moovBox) {
        throw new Error('Corrupt moov box');
      }

      return { moovView, moovBox };
    }

    offset += size;
  }

  throw new Error('moov box not found');
};

const readTrackDimensions = (dv: DataView, tkhdBox: QuickTimeBox | null) => {
  if (!tkhdBox || tkhdBox.end - 8 < tkhdBox.contentStart) {
    return null;
  }

  const width = readU32(dv, tkhdBox.end - 8) / 65536;
  const height = readU32(dv, tkhdBox.end - 4) / 65536;

  if (width > 0 && height > 0) {
    return { width, height };
  }

  return null;
};

const readTrackTimescale = (dv: DataView, mdhdBox: QuickTimeBox | null) => {
  if (!mdhdBox || mdhdBox.contentStart + 24 > mdhdBox.end) {
    return null;
  }

  const version = dv.getUint8(mdhdBox.contentStart);
  const timescaleOffset = version === 1 ? mdhdBox.contentStart + 20 : mdhdBox.contentStart + 12;

  if (timescaleOffset + 4 > mdhdBox.end) {
    return null;
  }

  const timescale = readU32(dv, timescaleOffset);
  return timescale > 0 ? timescale : null;
};

const readTrackHandlerType = (dv: DataView, hdlrBox: QuickTimeBox | null) => {
  if (!hdlrBox || hdlrBox.contentStart + 12 > hdlrBox.end) {
    return '';
  }

  return toStr(dv, hdlrBox.contentStart + 8);
};

const readTimeToSampleCount = (dv: DataView, sttsBox: QuickTimeBox | null) => {
  if (!sttsBox || sttsBox.contentStart + 8 > sttsBox.end) {
    return 0;
  }

  const entryCount = readU32(dv, sttsBox.contentStart + 4);
  let entryOffset = sttsBox.contentStart + 8;
  let sampleCount = 0;

  for (let i = 0; i < entryCount && entryOffset + 8 <= sttsBox.end; i++) {
    sampleCount += readU32(dv, entryOffset);
    entryOffset += 8;
  }

  return sampleCount;
};

const readSampleSizeInfo = (dv: DataView, stszBox: QuickTimeBox | null) => {
  if (!stszBox || stszBox.contentStart + 12 > stszBox.end) {
    return { sampleCount: 0, hasSampleBytes: null };
  }

  const fixedSampleSize = readU32(dv, stszBox.contentStart + 4);
  const sampleCount = readU32(dv, stszBox.contentStart + 8);

  if (!sampleCount) {
    return { sampleCount: 0, hasSampleBytes: false };
  }

  if (fixedSampleSize > 0) {
    return { sampleCount, hasSampleBytes: true };
  }

  let entryOffset = stszBox.contentStart + 12;
  let tableComplete = true;
  for (let i = 0; i < sampleCount; i++) {
    if (entryOffset + 4 > stszBox.end) {
      tableComplete = false;
      break;
    }

    if (readU32(dv, entryOffset) > 0) {
      return { sampleCount, hasSampleBytes: true };
    }

    entryOffset += 4;
  }

  return { sampleCount, hasSampleBytes: tableComplete ? false : null };
};

const readSampleDescriptionIndexes = (dv: DataView, stscBox: QuickTimeBox | null) => {
  const descriptionIndexes: number[] = [];
  if (!stscBox || stscBox.contentStart + 8 > stscBox.end) {
    return descriptionIndexes;
  }

  const entryCount = readU32(dv, stscBox.contentStart + 4);
  let entryOffset = stscBox.contentStart + 8;

  for (let i = 0; i < entryCount && entryOffset + 12 <= stscBox.end; i++) {
    descriptionIndexes.push(readU32(dv, entryOffset + 8));
    entryOffset += 12;
  }

  return uniqueNumbers(descriptionIndexes).sort((a, b) => a - b);
};

const inspectQuickTimeAudioTrack = (
  dv: DataView,
  trakBox: QuickTimeBox,
  isFragmentedMovie: boolean,
): UploadAudioTrackInspection | null => {
  const mdiaBox = getFirstChildBox(dv, trakBox, 'mdia');
  if (!mdiaBox) {
    return null;
  }

  const hdlrBox = getFirstChildBox(dv, mdiaBox, 'hdlr');
  if (readTrackHandlerType(dv, hdlrBox) !== 'soun') {
    return null;
  }

  const minfBox = getFirstChildBox(dv, mdiaBox, 'minf');
  const stblBox = minfBox ? getFirstChildBox(dv, minfBox, 'stbl') : null;
  const sttsBox = stblBox ? getFirstChildBox(dv, stblBox, 'stts') : null;
  const stscBox = stblBox ? getFirstChildBox(dv, stblBox, 'stsc') : null;
  const stszBox = stblBox ? getFirstChildBox(dv, stblBox, 'stsz') : null;
  const sampleSizeInfo = readSampleSizeInfo(dv, stszBox);
  const sampleCount = sampleSizeInfo.sampleCount || readTimeToSampleCount(dv, sttsBox);

  return {
    sampleDescriptionIndexes: readSampleDescriptionIndexes(dv, stscBox),
    sampleCount,
    hasSampleBytes: isFragmentedMovie && sampleCount === 0 ? null : sampleSizeInfo.hasSampleBytes,
  };
};

const inspectQuickTimeTrack = (
  dv: DataView,
  trakBox: QuickTimeBox,
): UploadVideoTrackInspection | null => {
  const mdiaBox = getFirstChildBox(dv, trakBox, 'mdia');
  if (!mdiaBox) {
    return null;
  }

  const hdlrBox = getFirstChildBox(dv, mdiaBox, 'hdlr');
  if (readTrackHandlerType(dv, hdlrBox) !== 'vide') {
    return null;
  }

  const mdhdBox = getFirstChildBox(dv, mdiaBox, 'mdhd');
  const minfBox = getFirstChildBox(dv, mdiaBox, 'minf');
  const stblBox = minfBox ? getFirstChildBox(dv, minfBox, 'stbl') : null;
  const stsdBox = stblBox ? getFirstChildBox(dv, stblBox, 'stsd') : null;
  const stscBox = stblBox ? getFirstChildBox(dv, stblBox, 'stsc') : null;
  const sttsBox = stblBox ? getFirstChildBox(dv, stblBox, 'stts') : null;
  const tkhdBox = getFirstChildBox(dv, trakBox, 'tkhd');
  const fallbackDimensions = readTrackDimensions(dv, tkhdBox);

  const codecTypes: string[] = [];
  const resolutions: string[] = [];

  if (stsdBox && stsdBox.contentStart + 8 <= stsdBox.end) {
    const entryCount = readU32(dv, stsdBox.contentStart + 4);
    let entryOffset = stsdBox.contentStart + 8;

    for (let i = 0; i < entryCount && entryOffset + 8 <= stsdBox.end; i++) {
      const entrySize = readU32(dv, entryOffset);
      const entryType = normalizeCodecType(toStr(dv, entryOffset + 4));

      if (!entrySize || entryOffset + entrySize > stsdBox.end) {
        break;
      }

      codecTypes.push(entryType);

      if (entrySize >= 36 && entryOffset + 36 <= entryOffset + entrySize) {
        const width = readU16(dv, entryOffset + 32);
        const height = readU16(dv, entryOffset + 34);
        if (width > 0 && height > 0) {
          resolutions.push(`${width}x${height}`);
        }
      }

      entryOffset += entrySize;
    }
  }

  if (!resolutions.length && fallbackDimensions) {
    resolutions.push(`${fallbackDimensions.width}x${fallbackDimensions.height}`);
  }

  const timescale = readTrackTimescale(dv, mdhdBox);
  const fpsValues: number[] = [];
  let totalSamples = 0;
  let totalTicks = 0;

  if (sttsBox && timescale && sttsBox.contentStart + 8 <= sttsBox.end) {
    const entryCount = readU32(dv, sttsBox.contentStart + 4);
    let entryOffset = sttsBox.contentStart + 8;

    for (let i = 0; i < entryCount && entryOffset + 8 <= sttsBox.end; i++) {
      const sampleCount = readU32(dv, entryOffset);
      const sampleDelta = readU32(dv, entryOffset + 4);

      if (sampleCount > 0 && sampleDelta > 0) {
        totalSamples += sampleCount;
        totalTicks += sampleCount * sampleDelta;
        const fps = normalizeFpsValue(timescale / sampleDelta);
        if (fps !== null) {
          fpsValues.push(fps);
        }
      }

      entryOffset += 8;
    }
  }

  const averageFps =
    timescale && totalSamples > 0 && totalTicks > 0
      ? normalizeFpsValue(totalSamples / (totalTicks / timescale))
      : null;

  const normalizedCodecTypes = uniqueStrings(codecTypes.map(normalizeCodecType));
  const normalizedResolutions = uniqueStrings(resolutions);
  const normalizedFpsValues = uniqueNumbers(
    averageFps === null ? fpsValues : [...fpsValues, averageFps],
  ).sort((a, b) => a - b);

  return {
    sampleDescriptionIndexes: readSampleDescriptionIndexes(dv, stscBox),
    codecTypes: normalizedCodecTypes,
    codecs: uniqueStrings(normalizedCodecTypes.map(normalizeCodecLabel)),
    resolutions: normalizedResolutions,
    fpsValues: normalizedFpsValues,
    averageFps,
  };
};

export async function inspectVideoFileForUpload(file: File): Promise<UploadVideoInspection> {
  const { moovView, moovBox } = await readQuickTimeMoovBuffer(file);
  const trackBoxes = listChildBoxes(moovView, moovBox.contentStart, moovBox.end, 'trak');
  const isFragmentedMovie = Boolean(getFirstChildBox(moovView, moovBox, 'mvex'));
  const trackInspections = trackBoxes
    .map((trakBox) => inspectQuickTimeTrack(moovView, trakBox))
    .filter((track): track is UploadVideoTrackInspection => Boolean(track));
  const audioTrackInspections = trackBoxes
    .map((trakBox) => inspectQuickTimeAudioTrack(moovView, trakBox, isFragmentedMovie))
    .filter((track): track is UploadAudioTrackInspection => Boolean(track));
  const codecTypes = trackInspections.reduce<string[]>(
    (acc, track) => acc.concat(track.codecTypes),
    [],
  );
  const codecs = trackInspections.reduce<string[]>(
    (acc, track) => acc.concat(track.codecs),
    [],
  );
  const resolutions = trackInspections.reduce<string[]>(
    (acc, track) => acc.concat(track.resolutions),
    [],
  );
  const fpsValues = trackInspections.reduce<number[]>(
    (acc, track) => acc.concat(track.fpsValues),
    [],
  );

  return {
    tracks: trackInspections,
    audioTracks: audioTrackInspections,
    codecTypes: uniqueStrings(codecTypes),
    codecs: uniqueStrings(codecs),
    resolutions: uniqueStrings(resolutions),
    fpsValues: uniqueNumbers(fpsValues).sort((a, b) => a - b),
  };
}

// Reads only MP4 box headers + the moov box (skips huge mdat). Much lighter than file.arrayBuffer().
export async function getMp4DimensionsFast(file: File): Promise<{ width: number; height: number }> {
  const readChunk = (start: number, len: number) =>
    new Promise<ArrayBuffer>((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result as ArrayBuffer);
      fr.onerror = rej;
      fr.readAsArrayBuffer(file.slice(start, start + len));
    });

  const readU32 = (dv: DataView, o: number) => dv.getUint32(o); // big-endian
  const readU64 = (dv: DataView, o: number) => Number(dv.getBigUint64(o));
  const toStr = (dv: DataView, o: number) =>
    String.fromCharCode(dv.getUint8(o), dv.getUint8(o + 1), dv.getUint8(o + 2), dv.getUint8(o + 3));

  let offset = 0;
  const fileSize = file.size;

  // 1) Walk top-level boxes by reading only their headers, skipping via size
  while (offset + 8 <= fileSize) {
    const hdrBuf = await readChunk(offset, 16); // enough for 32-bit or 64-bit header
    const dvh = new DataView(hdrBuf);
    let size = readU32(dvh, 0);
    const type = toStr(dvh, 4);
    let headerLen = 8;
    if (size === 1) {
      size = readU64(dvh, 8);
      headerLen = 16;
    }
    if (!size || size < headerLen) throw new Error('Corrupt MP4 box');

    if (type === 'moov') {
      // 2) Read only the moov box payload once
      const moovStart = offset + headerLen;
      const moovSize = size - headerLen;
      const moovBuf = await readChunk(moovStart, moovSize);
      const dvm = new DataView(moovBuf);

      // helper to iterate child boxes inside moov
      const readBoxes = (start: number, end: number, wanted: string) => {
        const out: number[] = [];
        let p = start;
        while (p + 8 <= end) {
          let sz = dvm.getUint32(p);
          const tp = toStr(dvm, p + 4);
          let hlen = 8;
          if (sz === 1) {
            sz = Number(dvm.getBigUint64(p + 8));
            hlen = 16;
          }
          if (!sz || p + sz > end) break;
          if (tp === wanted) out.push(p + hlen);
          p += sz;
        }
        return out;
      };

      const moovEnd = moovBuf.byteLength;
      const traks = readBoxes(0, moovEnd, 'trak');
      for (const trak of traks) {
        const tkhdArr = readBoxes(trak, moovEnd, 'tkhd');
        if (!tkhdArr.length) continue;

        // Find tkhd box boundaries to read last two u32 (fixed 16.16 width/height)
        const tkhdHeaderStart = tkhdArr[0] - 8;
        let tkhdSize = dvm.getUint32(tkhdHeaderStart);
        let tkhdHeaderLen = 8;
        if (tkhdSize === 1) {
          tkhdSize = Number(dvm.getBigUint64(tkhdHeaderStart + 8));
          tkhdHeaderLen = 16;
        }
        const tkhdEnd = tkhdHeaderStart + tkhdSize;

        const wFixed = dvm.getUint32(tkhdEnd - 8);
        const hFixed = dvm.getUint32(tkhdEnd - 4);
        const width = wFixed / 65536;
        const height = hFixed / 65536;
        if (width > 0 && height > 0) return { width, height };
      }
      throw new Error('No video track with dimensions found');
    }

    // Skip entire box (e.g., huge 'mdat') without reading it
    offset += size;
  }

  throw new Error('moov box not found');
}
